import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ServiceUnavailableStateService } from 'src/app/shared/services/service-unavailable-state.service';

/**
 * Detecta el error de "compañía inexistente": el backend responde HTTP 500 con
 * el wrapper {"status":false,"code":0,"response":null} (verificado en dev y prod).
 * En ese caso se debe redirigir a /404, NO a /503 (el backend sí respondió).
 * Usada por el interceptor y por los guards (LoginGuard).
 */
export function isCompanyNotFoundError(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse &&
    err.status >= 500 &&
    err.error !== null &&
    typeof err.error === 'object' &&
    (err.error as any).status === false &&
    (err.error as any).code === 0
  );
}

/**
 * Detecta caídas del backend Galavail (status 0 o >= 500) y redirige a /503
 * guardando la URL de la página donde se produjo el fallo.
 * Solo aplica a URLs del backend Galavail (mismo filtro que AuthInterceptor.requiresAuth);
 * las URLs de token (pathAuth0Token, pathUuid, pathAuditClientAccess) quedan excluidas.
 */
@Injectable()
export class ServiceUnavailableInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private stateService = inject(ServiceUnavailableStateService);

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isGalavailBackendUrl(req.url)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        // "Compañía inexistente" (500 con wrapper status:false) NO es una caída
        // del backend: se excluye para que el flujo normal redirija a /404.
        if (
          isPlatformBrowser(this.platformId) &&
          this.isBackendDown(err) &&
          !isCompanyNotFoundError(err)
        ) {
          this.handleBackendDown();
        }
        return throwError(() => err); // rethrow siempre
      })
    );
  }

  private isGalavailBackendUrl(url: string): boolean {
    return (
      url.startsWith(environment.pathService) ||
      url.startsWith(environment.pathServiceAv) ||
      url.startsWith(environment.pathAgencyEcommerceAPI.findLastMinute)
    );
  }

  private isBackendDown(err: unknown): err is HttpErrorResponse {
    return (
      err instanceof HttpErrorResponse &&
      (err.status === 0 || err.status >= 500)
    );
  }

  private handleBackendDown(): void {
    // La navegación en curso (si existe) indica a dónde iba el usuario.
    // window.location.pathname sigue apuntando a la URL anterior mientras la
    // navegación no termina, y router.url también está obsoleto en ese momento.
    const nav = this.router.getCurrentNavigation();
    const navTarget = nav
      ? (nav.finalUrl ?? nav.extractedUrl).toString()
      : null;

    if (navTarget !== null && navTarget.endsWith('/503')) {
      // Es el AppResolver de la propia página /503: no redirigir (evita loop infinito).
      return;
    }

    const browserPath = `${window.location.pathname}${window.location.search}`;
    if (navTarget === null && browserPath.endsWith('/503')) {
      // Falla una petición estando ya en /503 sin navegación en curso: no redirigir.
      return;
    }

    // IMPORTANTE: durante un reintento la URL del navegador es /503, pero el
    // objetivo real es navTarget (la URL guardada). Usar navTarget preserva
    // failedPath y no reinicia el contador de reintentos.
    const failedPath = navTarget ?? browserPath;
    this.stateService.registerFailure(
      failedPath,
      `${window.location.origin}${failedPath}`
    );

    const companyName = this.extractCompanyName(browserPath);
    if (!companyName) {
      return;
    }
    this.router.navigate([`${companyName}/503`], { replaceUrl: true }).catch(() => {
      // Navegación superseded: el guard u otro redirect ya gestiona el estado.
    });
  }

  private extractCompanyName(browserPath: string): string {
    // Fallo inicial: '/company/...'; reintento: '/company/503' (misma company).
    return browserPath.split('/')[1] || this.router.url.split('/')[1] || '';
  }
}
